import {
  fleetMapRunColor,
  fleetMapVehicleState,
  type FleetMapLabels,
  type FleetMapStop,
  type FleetMapStopState,
  type FleetMapTrailPoint,
  type FleetMapVehicle,
  type FleetMapVehicleState,
} from '@bonbon/types';

/** The ERP's fleet chips: moving is green, stopped grey, offline red. */
export const STATE_COLOR: Record<FleetMapVehicleState, string> = {
  MOVING: '#2e7d32',
  STOPPED: '#607d8b',
  OFFLINE: '#d32f2f',
};

export interface VehicleMarkerModel {
  telematicsVehicleId: string;
  position: google.maps.LatLngLiteral;
  state: FleetMapVehicleState;
  color: string;
  /** Degrees clockwise from north; null draws a dot instead of an arrow. */
  heading: number | null;
  /** Plate, then nickname when there is one. */
  title: string;
  /** What a screen reader says: the plate and the state in words. */
  ariaLabel: string;
}

export interface StopMarkerModel {
  id: string;
  position: google.maps.LatLngLiteral;
  state: FleetMapStopState;
  /** The colour of the stop's run. */
  color: string;
  /** The number drawn in the dot; empty draws a plain dot. */
  label: string;
  /** What a screen reader says: the word for a stop and its number; empty when unknown. */
  ariaLabel: string;
}

/** Wraps any finite heading into [0, 360). */
export function normaliseHeading(heading: number | null): number | null {
  if (heading === null || !Number.isFinite(heading)) return null;
  return ((heading % 360) + 360) % 360;
}

/**
 * The marker for each van at `now`. The state is derived here, not taken from
 * the host, so a van that goes quiet turns OFFLINE on the next clock tick
 * without any message arriving.
 */
export function buildMarkerModels(
  vehicles: readonly FleetMapVehicle[],
  labels: FleetMapLabels,
  now: Date,
): VehicleMarkerModel[] {
  return vehicles.map((vehicle) => {
    const state = fleetMapVehicleState(vehicle, now);
    const title =
      vehicle.subtitle === null || vehicle.subtitle === ''
        ? vehicle.title
        : `${vehicle.title} · ${vehicle.subtitle}`;
    return {
      telematicsVehicleId: vehicle.telematicsVehicleId,
      position: { lat: vehicle.lat, lng: vehicle.lng },
      state,
      color: STATE_COLOR[state],
      heading: normaliseHeading(vehicle.heading),
      title,
      ariaLabel: `${vehicle.title}: ${labels.vehicleState[state]}`,
    };
  });
}

/** The marker for each delivery stop: the colour comes from its run. */
export function buildStopModels(
  stops: readonly FleetMapStop[],
  labels: FleetMapLabels,
): StopMarkerModel[] {
  return stops.map((stop) => ({
    id: stop.id,
    position: { lat: stop.lat, lng: stop.lng },
    state: stop.state,
    color: fleetMapRunColor(stop.group),
    label: stop.label,
    ariaLabel: [labels.stop ?? '', stop.label].filter((part) => part !== '').join(' '),
  }));
}

/** Furthest the camera zooms in by itself, so one van is not a blur of roofs. */
export const MAX_FIT_ZOOM = 15;

/**
 * Below this extent (about a kilometre) fitting would zoom in past
 * {@link MAX_FIT_ZOOM}: centre on the spot at that zoom instead.
 */
const MIN_FIT_EXTENT_DEGREES = 0.01;

export type CameraTarget =
  | { kind: 'bounds'; bounds: google.maps.LatLngBoundsLiteral }
  | { kind: 'point'; center: google.maps.LatLngLiteral; zoom: number };

/**
 * Where to point the camera to show the points: fit them, unless they are
 * (nearly) one spot (one van, or all of them at the depot), where a fit would
 * zoom to the maximum.
 */
export function cameraFor(
  points: readonly google.maps.LatLngLiteral[],
): CameraTarget | null {
  const bounds = boundsOf(points);
  if (bounds === null) return null;
  const height = bounds.north - bounds.south;
  const width = bounds.east - bounds.west;
  if (height < MIN_FIT_EXTENT_DEGREES && width < MIN_FIT_EXTENT_DEGREES) {
    return {
      kind: 'point',
      center: {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2,
      },
      zoom: MAX_FIT_ZOOM,
    };
  }
  return { kind: 'bounds', bounds };
}

/** The smallest box around the points, or null for none. */
export function boundsOf(
  points: readonly google.maps.LatLngLiteral[],
): google.maps.LatLngBoundsLiteral | null {
  const first = points[0];
  if (first === undefined) return null;
  let north = first.lat;
  let south = first.lat;
  let east = first.lng;
  let west = first.lng;
  for (const point of points) {
    north = Math.max(north, point.lat);
    south = Math.min(south, point.lat);
    east = Math.max(east, point.lng);
    west = Math.min(west, point.lng);
  }
  return { north, south, east, west };
}

export function trailPath(
  points: readonly FleetMapTrailPoint[],
): google.maps.LatLngLiteral[] {
  return points.map((point) => ({ lat: point.lat, lng: point.lng }));
}
